import React, { useEffect, useRef } from 'react'
import { LockClosedIcon } from '@heroicons/react/24/outline'
import { useMozna } from '../../hooks/usePlan'
import { BramkaTresci } from '../auth/BramkaLogowania'
import { FUNKCJE_PREMIUM, nazwaFunkcji } from '../../config/plany'
import { useOknoPremium } from './OknoPremiumContext'

/**
 * Bramki planu Premium (FE-02): kłódka, karta w miejscu funkcji i opakowanie łączące bramkę logowania z planem.
 *
 * ═══ DWA POZIOMY ═══
 * 1. Bez konta: bramka logowania jak dotąd (`BramkaTresci`, klucz z config/bramki.js), z jej wyjątkami
 *    (np. OSD i PSP otwarte na serwerze deweloperskim).
 * 2. Zalogowany bez funkcji w planie (Free, koniec próby): karta Premium z oknem „Przejdź na Premium”.
 *    Bez trybu tylko do odczytu (decyzja właściciela 2026-10-19).
 *
 * Zasłona w interfejsie, nie kontrola dostępu: tę robi backend (402). Komponent pod zasłoną w trybie `naklad`
 * renderuje się rozmyty, więc NIE używaj go dla treści, która sama woła endpoint Premium (dostałaby 402 i okno
 * otworzyłoby się samo). Wtedy `tryb="zamiast"`.
 */

/** Plakietka „Premium” przy nazwie funkcji (menu, kafelki, przyciski). */
// Wygląd: kolor niesie tylko ten mały znacznik, tło zostaje białe, bez wielkich liter (uwaga właściciela o ekranach
// „jak z generatora”, second-brain/10_PAMIEC_AGENTA/wyglad-bez-llm-kafelkow.md).
export const KlodkaPremium = ({ className = '' }) => (
  <span title="W planie Premium"
        className={`inline-flex flex-shrink-0 items-center rounded-full border border-amber-300 px-1.5 py-px text-[11px]
                    font-semibold leading-4 text-amber-700 ${className}`}>
    Premium
    <span className="sr-only"> (funkcja planu Premium)</span>
  </span>
)

/** Czy przy odnośniku do funkcji pokazać plakietkę Premium. Dopóki plan się ustala: nie (żeby nie mignęła). */
export const useWymagaPremium = (funkcja) => {
  const { powod } = useMozna(funkcja || '')
  return Boolean(funkcja) && powod === 'premium'
}

const wielkaLitera = (t) => (t ? t.charAt(0).toUpperCase() + t.slice(1) : t)

/** Karta w miejscu funkcji dla planu bez tej funkcji. `kompaktowa` do ciasnych miejsc (sekcja karty klienta). */
export const KartaPremium = ({ funkcja, tytul, opis, kompaktowa = false }) => {
  const { otworz } = useOknoPremium()
  const opisFunkcji = FUNKCJE_PREMIUM[funkcja]?.opis
  return (
    <div className={`w-full rounded-2xl border border-gray-200 bg-white text-center dark:border-gray-700
                     dark:bg-gray-900 ${kompaktowa ? 'p-4' : 'max-w-md p-6 shadow-xl sm:p-7'}`}>
      {!kompaktowa && (
        <span className="mb-4 inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-blue-50 text-blue-600">
          <LockClosedIcon className="h-6 w-6" aria-hidden="true" />
        </span>
      )}
      <h3 className={`flex items-center justify-center gap-1.5 font-bold text-gray-900 dark:text-gray-100
                      ${kompaktowa ? 'text-sm' : 'text-lg'}`}>
        {kompaktowa && <LockClosedIcon className="h-4 w-4 flex-shrink-0 text-gray-400" aria-hidden="true" />}
        {tytul || `${nazwaFunkcji(funkcja)} w planie Premium`}
      </h3>
      <p className={`mt-1.5 leading-6 text-gray-600 dark:text-gray-300 ${kompaktowa ? 'text-xs' : 'mt-2 text-sm'}`}>
        {opis || (opisFunkcji ? `${wielkaLitera(opisFunkcji)}.` : 'Ta funkcja jest w planie Premium.')}
      </p>
      <button
        type="button" onClick={() => otworz(funkcja)}
        className={`mt-4 flex w-full items-center justify-center rounded-lg bg-blue-600 font-semibold text-white
                    shadow-sm transition-colors hover:bg-blue-500 active:bg-blue-700
                    ${kompaktowa ? 'px-3 py-2 text-xs' : 'px-4 py-2.5 text-sm'}`}
      >
        Zobacz, co daje Premium
      </button>
    </div>
  )
}

/**
 * Sama warstwa planu, bez bramki logowania. Niezalogowany przechodzi dalej: o nim decyduje bramka logowania
 * wyżej (albo jej brak, np. OSD na serwerze deweloperskim).
 */
export const WarstwaPremium = ({ funkcja, children, tryb = 'zamiast', tytul, opis, kompaktowa = true, maxWysokosc = 'max-h-80' }) => {
  const { mozna, ustalanie, powod } = useMozna(funkcja)
  const tloRef = useRef(null)

  useEffect(() => {
    const el = tloRef.current
    if (el) el.inert = true
  })

  if (ustalanie) return <p className="py-6 text-center text-sm text-gray-400">Wczytywanie…</p>
  if (mozna || powod === 'bez_konta') return children

  if (tryb === 'zamiast') {
    return (
      <div className="flex justify-center py-2">
        <KartaPremium funkcja={funkcja} tytul={tytul} opis={opis} kompaktowa={kompaktowa} />
      </div>
    )
  }

  return (
    <div className="relative">
      <div className={`relative overflow-hidden ${maxWysokosc}`} aria-hidden="true">
        <div ref={tloRef} className="pointer-events-none select-none opacity-70 blur-[3px]">{children}</div>
        <div className="pointer-events-none absolute inset-x-0 bottom-0 h-24 bg-gradient-to-b from-transparent to-white
                        dark:to-gray-900" />
      </div>
      <div className="absolute inset-0 flex items-center justify-center px-4">
        <KartaPremium funkcja={funkcja} tytul={tytul} opis={opis} />
      </div>
    </div>
  )
}

/**
 * Bramka funkcji: najpierw logowanie (klucz `bramka` z config/bramki.js), potem plan (klucz `funkcja`
 * z config/plany.js). Teksty karty logowania: `tytulLogowania`, `opisLogowania`.
 */
export const BramkaFunkcji = ({
  funkcja, bramka = null, children, tryb = 'zamiast', tytul, opis, tytulLogowania, opisLogowania, kompaktowa = true,
  maxWysokosc,
}) => (
  <BramkaTresci bramka={bramka} tryb={tryb} tytul={tytulLogowania} opis={opisLogowania} kompaktowa={kompaktowa}
                maxWysokosc={maxWysokosc}>
    <WarstwaPremium funkcja={funkcja} tryb={tryb} tytul={tytul} opis={opis} kompaktowa={kompaktowa} maxWysokosc={maxWysokosc}>
      {children}
    </WarstwaPremium>
  </BramkaTresci>
)

export default BramkaFunkcji
